import {
  HISTORIES_ACTION_NAME,
  PROCESS_STATUS,
  SORT_METHODS,
  CONFIG_FIELD_ORDER,
  UPLOAD_TYPE,
  SIMULATION_TYPE,
  TARGET_MODE,
  PATTERN_SIMULATION_VERSION,
  VERSION_TYPE,
} from "src/constant/common";
import { cloneDeep, endsWith, merge, omit } from "lodash";
import { mcsDefaultData } from "src/components/GNCSimulator6DOF/form/mcsDefaultData";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import times from "lodash/times";
import max from "lodash/max";
import flatMap from "lodash/flatMap";
import { FORM_NAME as FORM_NAME_3DOF } from "src/components/Common/Modal/ModalUploadFile3DOF";
import { FORM_NAME as FORM_NAME_6DOF } from "src/components/Common/Modal/ModalUploadFile6DOF";
import { REACT_APP_SIMULATION_3DOF_VERSION } from "src/constant/environment";

function humanFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units = si ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"] : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + " " + units[u];
}

const transformDataVersionsToOptions = (array) => {
  const result = [];

  array.map((item) =>
    result.push({
      label: item.name,
      value: item.name,
      execute_files: transformDataExecutionFileToOptions(item.execute_files),
    })
  );

  return result;
};

const transformDataExecutionFileToOptions = (array) => {
  const result = [];

  array.map((item) => result.push({ label: item.name, value: item.name }));

  return result;
};

const getFileName = (path) => {
  if (!path) return "";
  const regex = /\/?([^/]+\.json|[^/]+\.csv|[^/]+\.xlsx|[^/]+\.xlsm|[^/]+\.kml)(?:\?|$)/;
  const match = path.match(regex);
  const fileName = match ? match[1] : "";
  return fileName;
};

const getFileUploadName = (path) => {
  if (!path) return "";

  // Decode the path multiple times to handle nested encoding
  let decodedPath = path;
  try {
    while (decodedPath !== decodeURIComponent(decodedPath)) {
      decodedPath = decodeURIComponent(decodedPath);
    }
  } catch (error) {
    console.log("Error decoding path:", path);
  }

  const regex = /\/?([^/]+\.json|[^/]+\.csv|[^/]+\.xlsx|[^/]+\.xlsm|[^/]+\.kml)(?:\?|$)/;
  const match = decodedPath.match(regex);
  const fileName = match ? match[1] : "";
  return fileName;
};

const getFileType = (path) => {
  if (!path) return "";
  return path.split(".").pop();
};

const uniqArray = (arr) => {
  const seen = new Set();
  const uniqueByDataField = arr.filter((item) => {
    if (!seen.has(item.dataField)) {
      seen.add(item.dataField);
      return true;
    }
    return false;
  });
  return uniqueByDataField;
};

const handleScrollTo = (top, left, id) => {
  setTimeout(() => {
    let leftPosition = left;
    if (id) {
      const element = document.getElementById(id);
      leftPosition = parseInt(element?.getBoundingClientRect().top - left);
    }
    window.scrollTo(top, leftPosition - 85);
  }, 500);
};

const handleScrollToTop = (elementId = null, behavior = "instant") => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior, block: "start" });
  } else {
    window.scrollTo({ top: 0, behavior });
  }
};

const scrollToLatestTag = (ref, valueContainerClass, multiValueClass) => {
  setTimeout(() => {
    if (ref.current && ref.current.controlRef) {
      const valueContainer = ref.current.controlRef.querySelector(valueContainerClass);
      if (valueContainer) {
        const multiValues = valueContainer.querySelectorAll(multiValueClass);
        if (multiValues.length > 0) {
          const lastTag = multiValues[multiValues.length - 1];
          lastTag.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "end",
          });
        } else {
          valueContainer.scrollLeft = valueContainer.scrollWidth;
        }
      }
    }
  }, 100);
};

const sortData = (data, key, methods = SORT_METHODS.ASC) => {
  return data.sort(function (a, b) {
    let firstChar = a;
    let secondChar = b;
    if (methods === SORT_METHODS.DESC) {
      firstChar = b;
      secondChar = a;
    }
    // Guard against null/undefined or non-string values so the sort never throws.
    return String(firstChar?.[key] ?? "").localeCompare(String(secondChar?.[key] ?? ""));
  });
};

const uuid = () => {
  const timestamp = Date.now();
  const randomSegment = Math.floor(Math.random() * 10000);
  const uniqueId = timestamp * 10000 + randomSegment;
  return uniqueId;
};

const extractIDsFromString = (str) => {
  if (!str) return ["", ""];
  const regex = /([^/]+)\/([^/]+)\.json/;
  const match = str.match(regex);
  if (match) {
    return [match[1], match[2]];
  } else {
    return ["", ""];
  }
};

const transformProcessDataToOptions = (array) => {
  const result = [];

  array.map((item) =>
    result.push({
      label: item.name,
      value: item.id,
    })
  );

  return result;
};

const getConfigFromId = (processId, array, key) => {
  const result = array.filter((item) => item.id === processId);
  if (!result.length || !Object.keys(result[0]?.config).length) return {};
  const config = result[0].config?.[key] || {};
  const outputConfig = [];
  CONFIG_FIELD_ORDER.forEach((key) => {
    if (config[key]) {
      outputConfig.push({ name: key, type: config[key] });
    }
  });
  return outputConfig;
};

const getConfigFileName = (config) => {
  const outputPath = [];
  if (config?.no_wind_path) {
    outputPath.push(getFileName(config.no_wind_path));
  }
  if (config?.blow_path) {
    outputPath.push(getFileName(config.blow_path));
  }
  if (config?.atmospheric_pressure_data_path) {
    outputPath.push(getFileName(config.atmospheric_pressure_data_path));
  }
  return outputPath.join(", ");
};

const transformThrustCalculation = (data) => {
  return {
    name: data.name,
    number_of_stage: parseInt(data.number_of_stage, 10),
    input_data: {
      number_of_stage: parseInt(data.number_of_stage, 10),
      stages: data.stage.map((s, index) => ({
        stage: data.stage?.length - index,
        total_propellant_mass: {
          value: parseFloat(s.total_propellant_mass, 10),
          unit: s.unit,
        },
      })),
      modes: [].concat(
        ...data.stage.map((s, index) =>
          s.modes.map((m) => ({
            stage: data.stage?.length - index,
            name: m.name,
            engine_components: m.engine_components.map((c) => ({
              component_id: c.component_id,
              number_of_unit: parseInt(c.number_of_unit, 10),
            })),
            propellant: {
              value: parseFloat(s.total_propellant_mass, 10) * (parseFloat(m.propellant_percentage, 10) / 100),
              unit: s.unit,
            },
            propellant_percentage: {
              value: parseFloat(m.propellant_percentage, 10),
              unit: "%",
            },
            mode: m.mode,
          }))
        )
      ),
    },
  };
};

const reverseTransformThrustCalculation = (data) => {
  // Helper function to find modes for a particular stage
  function getModesForStage(stageNumber, modes) {
    return modes
      .filter((mode) => mode.stage === stageNumber)
      .map((mode) => ({
        name: mode.name,
        propellant_percentage: mode.propellant_percentage.value.toString(),
        mode: mode.mode,
        engine_components: mode.engine_components.map((comp) => ({
          component_id: comp.component_id,
          number_of_unit: comp.number_of_unit.toString(),
        })),
      }));
  }

  // Helper function to calculate total propellant mass based on modes and to get unit
  function calculateTotalPropellantMassAndUnit(stageNumber, modes) {
    let totalMass = 0;
    let unit = "";
    modes
      .filter((mode) => mode.stage === stageNumber)
      .forEach((mode) => {
        totalMass += mode.propellant.value;
        unit = mode.propellant.unit; // Assuming all propellant units are the same within a stage
      });
    return { totalMass, unit };
  }

  return {
    name: `Copy of ${data.name}`,
    number_of_stage: data.number_of_stage,
    stage: data.input_data.stages.map((s) => {
      const { totalMass, unit } = calculateTotalPropellantMassAndUnit(s.stage, data.input_data.modes);
      return {
        total_propellant_mass: s?.total_propellant_mass?.value || totalMass.toString(),
        unit: s?.total_propellant_mass?.unit || unit,
        modes: getModesForStage(s.stage, data.input_data.modes),
      };
    }),
  };
};

const roundToTwoDecimals = (inputNum, force) => {
  let num = Number(inputNum);
  if (isNaN(num) || (Number.isInteger(num) && !force)) return inputNum;
  return num.toFixed(2);
};

const mergeArrays = (array1, array2) => {
  if (!array2 || !Array.isArray(array2)) return array1;
  return array1.map((item, index) => {
    return { ...item, ...array2[index] };
  });
};

const mergeUniqueObjects = (arr1, arr2) => {
  const mergedArray = [...arr1, ...arr2];
  const seenItems = new Set();

  const serialize = (obj) => JSON.stringify(obj);
  const uniqueItems = mergedArray.reduce((uniqueList, item) => {
    const itemString = serialize(item);

    if (!seenItems.has(itemString)) {
      uniqueList.push(item);
      seenItems.add(itemString);
    }

    return uniqueList;
  }, []);

  return uniqueItems;
};

const getChartData = (mainList, listOfPoints = []) => {
  if (!Array.isArray(mainList)) return { data: [], labels: [] };

  const labels = [];
  const data = [];
  const pointRadius = [];

  let combinedList = [...mainList];

  if (listOfPoints?.length > 0) {
    const uniqueList = mergeUniqueObjects(mainList, listOfPoints);
    const formattedPoints = uniqueList.map((point) => {
      if (listOfPoints?.find((l) => l[0] === point[0] && l[1] === point?.[1])) {
        return { ...point, isPoint: true };
      }

      return point;
    });

    combinedList = [...formattedPoints];
  }

  combinedList
    .filter((item) => typeof item[0] === "number")
    .sort((a, b) => a[0] - b[0])
    .forEach((item) => {
      labels.push(item[0]);
      data.push(item[1]);
      pointRadius.push(item.isPoint ? 3 : 0);
    });

  const uniqueListPoints = Array.from(new Set(listOfPoints.map((point) => point[0])));

  return {
    labels,
    data,
    pointRadius,
    listPoint: uniqueListPoints,
  };
};

const uniqueArray = (array, key) => {
  const uniqueArray = [];
  const ids = new Set();

  array.forEach((item) => {
    if (!ids.has(item[key])) {
      ids.add(item[key]);
      uniqueArray.push(item);
    }
  });

  return uniqueArray;
};

const isCSVContent = (content) => {
  if (typeof content !== "string") {
    return false;
  }
  const csvPattern = /^,?(?:[^,\n]+,)*[^,\n]+(?:\n|$)/;
  return csvPattern.test(content);
};

const MAPPING_CONTENT_CHANGE_TO_TEXT = (t, _, id, new_value) => ({
  [HISTORIES_ACTION_NAME.UPDATE_ROCKET_MODEL]: () => t("logs.model"),
  [HISTORIES_ACTION_NAME.UPDATE_ENGINE_COMPONENT]: () => t("logs.engined"),
  [HISTORIES_ACTION_NAME.UPDATE_COMPONENT_DETAIL]: () => t("logs.components"),
  [HISTORIES_ACTION_NAME.ADD_NEW_CALCULATION]: () => {
    return t("logs.add_new_calculation", {
      unit: Array.isArray(new_value) ? (new_value?.length > 1 ? "s" : "") : "",
      ids: Array.isArray(new_value) ? new_value?.map((i) => i?.id)?.join(", ") : "",
    });
  },
  [HISTORIES_ACTION_NAME.RUN_SIMULATION]: () => {
    if (new_value?.simulator_status === PROCESS_STATUS.SUCCEEDED) {
      return t("logs.run_simulation_success", { status: PROCESS_STATUS.SUCCEEDED });
    }

    return t("logs.run_simulation_fail", { status: PROCESS_STATUS.FAILED });
  },
  [HISTORIES_ACTION_NAME.CALCULATE_THRUST]: () => {
    if (new_value?.status === PROCESS_STATUS.SUCCEEDED) {
      return t("logs.thrust_success", { status: PROCESS_STATUS.SUCCEEDED, id });
    }

    return t("logs.thrust_fail", { status: PROCESS_STATUS.FAILED, id });
  },
  [HISTORIES_ACTION_NAME.SAVE_UNPROCESSED_COMPONENT_DETAILS]: () => t("logs.save_unprocessed_component_details"),
});

const sumArrayWithKey = (data, key) => {
  if (data?.length === 0) return;

  return data.reduce((accumulator, currentValue) => {
    if (!currentValue[key]) return accumulator;

    return preciseAdd(accumulator, parseFloat(currentValue[key]));
  }, 0);
};

const mergeChildren = (arr) => {
  let result = [];

  const flatten = (array) => {
    for (let obj of array) {
      if (obj.children) {
        result = result.concat(obj.children);
        flatten(obj.children); // Recursively flatten the children
      }
    }
  };

  flatten(arr?.children);
  return result;
};

const findMaxMin = (data, key) => {
  // Create an array of objects with parsed values alongside their original values
  const parsedData = data
    .map((item) => {
      const value = item[key];
      // Determine if the value can be parsed to a number
      const parsedValue = typeof value === "string" && !isNaN(value) ? parseFloat(value) : value;
      return { original: value, parsed: parsedValue };
    })
    .filter((item) => typeof item.parsed === "number" && (Boolean(item.parsed) || item.parsed === 0)); // Ensure 0 is included

  // Extract original values for max and min by finding max and min parsed values
  const maxParsedValue = parsedData.length ? Math.max(...parsedData.map((item) => item.parsed)) : undefined;
  const minParsedValue = parsedData.length ? Math.min(...parsedData.map((item) => item.parsed)) : undefined;

  const maxOriginalValue = parsedData.find((item) => item.parsed === maxParsedValue)?.original;
  const minOriginalValue = parsedData.find((item) => item.parsed === minParsedValue)?.original;

  return { max: maxOriginalValue, min: minOriginalValue };
};

const calculateProperties = (nodes) => {
  // Recursive function to process each node
  const processNode = (node) => {
    if (node.type === "component") {
      // If the node is a component, return its properties
      return [{ length: node.length, l_axis_position: node.l_axis_position || 0 }];
    } else if (node.children && node.children.length > 0) {
      // If the node has children, process each child
      let childResults = [];
      node.children.forEach((child) => {
        childResults = childResults.concat(processNode(child));
      });
      // Calculate the sum using the calculateSum function
      const calculatedSum = calculateRocketLength(childResults);
      const newNode = { totalLength: calculatedSum };
      // Return the aggregated results to parent nodes
      return [newNode];
    }
    return [];
  };
  // Process each top-level node
  nodes.forEach((node) => {
    node.totalLength = processNode(node)[0].totalLength;
  });
  return nodes;
};

const calculateRocketLength = (items) => {
  if (!items || items.length === 0) return 0;

  // Reduce items to a map of l_axis_position with maximum lengths
  const axisLengths = items.reduce((map, { length, l_axis_position }) => {
    if (!map[l_axis_position] || map[l_axis_position] < length) {
      map[l_axis_position] = length;
    }
    return map;
  }, {});

  // Convert the map to an array and sort by l_axis_position
  const sortedAxisLengths = Object.entries(axisLengths).map(([l_axis_position, length]) => ({
    total: preciseAdd(l_axis_position, length),
    l_axis_position,
  }));

  return findMaxTotalAndMinLengthDifference(sortedAxisLengths) || 0;
};

const findMaxTotalAndMinLengthDifference = (arr) => {
  const { maxTotal, minLength } = arr.reduce(
    (acc, item) => {
      const total = parseFloat(item.total);
      const length = parseFloat(item.l_axis_position);
      return {
        maxTotal: total > acc.maxTotal ? total : acc.maxTotal,
        minLength: length < acc.minLength ? length : acc.minLength,
      };
    },
    { maxTotal: -Infinity, minLength: Infinity }
  );

  return preciseSubtract(maxTotal, minLength);
};

const preciseAdd = (num1, num2) => {
  const factor = Math.pow(10, Math.max(countDecimalPlaces(num1), countDecimalPlaces(num2)));
  return (Math.round(num1 * factor) + Math.round(num2 * factor)) / factor;
};

const preciseSubtract = (num1, num2) => {
  // Helper function to count decimal places
  const countDecimalPlaces = (num) => {
    if (Math.floor(num) === num) return 0;
    return num.toString().split(".")[1].length || 0;
  };

  // Determine the maximum number of decimal places
  const factor = Math.pow(10, Math.max(countDecimalPlaces(num1), countDecimalPlaces(num2)));

  // Perform subtraction with the factor to handle precision
  return (Math.round(num1 * factor) - Math.round(num2 * factor)) / factor;
};

const countDecimalPlaces = (num) => {
  if (!num) return 0;
  if (Math.floor(num) === num) return 0;
  return num.toString().split?.(".")?.[1]?.length || 0;
};

const calculateCenterOfGravity = (items) => {
  if (!items || items?.length === 0) return 0;
  let sumMass = 0;
  let sumGravityX = 0;
  let sumGravityY = 0;
  let sumGravityZ = 0;

  items?.forEach((item) => {
    sumMass = preciseAdd(sumMass, item?.mass);
    sumGravityX = preciseAdd(sumGravityX, parseFloat(item?.center_of_gravity_x) * parseFloat(item?.mass));
    sumGravityY = preciseAdd(sumGravityY, parseFloat(item?.center_of_gravity_y) * parseFloat(item?.mass));
    sumGravityZ = preciseAdd(sumGravityZ, parseFloat(item?.center_of_gravity_z) * parseFloat(item?.mass));
  });

  return {
    center_of_gravity_x: sumGravityX / sumMass,
    center_of_gravity_y: sumGravityY / sumMass,
    center_of_gravity_z: sumGravityZ / sumMass,
  };
};

const formatStringToNumber = (s) => {
  // Trim the input to remove any leading/trailing whitespace
  s = s.trim();

  // Regular expression to check if it's numeric or in "0.x", "-0.x" or ".x", "-.x" format
  if (!/^-?\d*(\.\d+)?$/.test(s)) {
    // Regular expression to extract digits and a single decimal point
    const extracted = s.match(/[\d.]/g);

    if (extracted) {
      // Join the matched parts
      let result = extracted.join("");

      // Ensure only one decimal point is present
      const decimalIdx = result.indexOf(".");
      if (decimalIdx !== -1) {
        result = result.slice(0, decimalIdx + 1) + result.slice(decimalIdx + 1).replace(/\./g, "");
      }

      return result;
    }
    return 0;
  }

  // Special case for zero, handle multiple leading zeros
  if (/^0+(\.0+)?$/.test(s)) {
    return "0";
  }

  // Handle cases like '.0099' and '-.0099'
  if (s.startsWith(".")) {
    return "0" + s;
  }

  if (s.startsWith("-.")) {
    return "-0" + s.slice(1);
  }

  // Special cases for '0', '-0', '0.x', and '-0.x'
  if (s === "0" || s === "-0" || /^-?0\.\d+$/.test(s)) {
    return s;
  }

  // Handle leading zeros for negative numbers
  if (s.startsWith("-")) {
    return "-" + s.slice(1).replace(/^0+/, "");
  }

  // Handle leading zeros for positive numbers
  return s.replace(/^0+/, "");
};

const deepEqual = (x, y) => {
  const isObject = (obj) => obj && typeof obj === "object";

  if (isObject(x) && isObject(y)) {
    if (Object.keys(x).length !== Object.keys(y).length) {
      return false;
    }

    return Object.keys(x).every((key) => deepEqual(x[key], y[key]));
  } else {
    // eslint-disable-next-line
    return x == y;
  }
};

const getClusterCenters = (arr, gap) => {
  arr.sort((a, b) => a.value - b.value);

  const clusters = [];
  let currentCluster = [];

  arr.forEach(({ value }) => {
    if (currentCluster.length === 0) {
      currentCluster.push(value);
      return;
    }

    // For the first cluster, use the first value for gap comparison
    if (clusters.length === 0) {
      const firstValue = currentCluster[0];
      if (value - firstValue <= gap) {
        currentCluster.push(value);
        return;
      }
    } else {
      // For other clusters, use the middle value for gap comparison
      const middleIndex = Math.floor(currentCluster.length / 2);
      const middleValue = currentCluster[middleIndex];
      if (value - middleValue <= gap) {
        currentCluster.push(value);
        return;
      }
    }

    clusters.push([...currentCluster]);
    currentCluster = [value];
  });

  if (currentCluster.length > 0) {
    clusters.push(currentCluster);
  }

  return clusters.map((cluster, index) => {
    if (index === 0) {
      // For the first cluster, return the first item
      return { value: cluster[0] };
    }
    if (index === clusters.length - 1) {
      // For the last cluster, return the maximum value
      const maxValue = Math.max(...cluster);
      return { value: maxValue };
    }

    // For other clusters, return the middle value
    const middleIndex = Math.floor(cluster.length / 2);
    return { value: cluster[middleIndex] };
  });
};

// This function removes __comment, unnecessary keys, and flattens input_data
const prepareData6DOF = (data) => {
  const clonedData = cloneDeep(data);
  const { input_data, ...restData } = clonedData;

  // Keys to exclude from vehicle data
  const excludedVehicleKeys = ["omega_twd", "ispv", "nozzle_area", "engine_model", "TVC_actuator_model", "RCS_model"];

  // Process vehicle data by excluding specific keys
  const processVehicleData = (vehicle) =>
    Object.fromEntries(Object.entries(vehicle || {}).filter(([key]) => !excludedVehicleKeys.includes(key)));

  // Recursively flatten "value" property and remove "unit" and "__comment"
  const cleanNestedObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj || {}).map(([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          const { value: val, ...rest } = value;
          return [key, val !== undefined ? val : cleanNestedObject(rest)];
        }
        return [key, value];
      })
    );

  // Apply transformations
  const cleanedInputData = cleanNestedObject({
    ...omit(input_data, ["IMU_sensor", "controller_settings"]),
    vehicle: processVehicleData(input_data?.vehicle),
  });

  return {
    ...restData,
    ...cleanedInputData,
  };
};

const extractString = (data, fromStr = "", endStr = "") => {
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const pattern = new RegExp(escapeRegExp(fromStr) + "(.*?)" + escapeRegExp(endStr));
  const match = data.match(pattern);

  if (match) {
    return fromStr + match[1];
  }
  return data;
};

const mapFormDataToDefaultData6DOF = (formData, dataDetail) => {
  let dataDetailClone = cloneDeep(dataDetail);
  const updateDataDetailBySimType = (defaultData, simulationType) => {
    if (simulationType === 1) {
      defaultData.input_data.MCS = {
        ...merge({}, mcsDefaultData, defaultData?.input_data?.MCS),
      };
    } else {
      delete defaultData.input_data.MCS;
      defaultData.input_data = {
        ...merge({}, defaultData?.input_data, dataDetail?.input_data),
      };
    }
    return defaultData;
  };

  dataDetailClone = updateDataDetailBySimType(dataDetailClone, formData?.type);

  const convertStringToNumberForFormData = (obj) =>
    Object.entries(obj).reduce((result, [key, value]) => {
      if (typeof value === "string") {
        result[key] = value.includes(",")
          ? value.split(",").map((item) => parseFloat(formatStringToNumber(item.trim())))
          : parseFloat(formatStringToNumber(value));
      } else if (typeof value === "object" && !Array.isArray(value)) {
        result[key] = convertStringToNumberForFormData(value);
      } else {
        result[key] = value;
      }
      return result;
    }, {});

  const convertSpecificKeys = (obj) => {
    const keysToConvert = ["main", "vehicle", "environment", "initial_condition", "target"];

    if (formData?.type === 1) {
      keysToConvert.push("MCS");
    }

    return keysToConvert.reduce(
      (result, key) => ({
        ...result,
        [key]: obj[key] && typeof obj[key] === "object" ? convertStringToNumberForFormData(obj[key]) : obj[key],
      }),
      { ...obj }
    );
  };

  const mapValues = (form, defaultData) =>
    Object.entries(defaultData).reduce((result, [key, value]) => {
      if (value?.value !== undefined) {
        result[key] = { ...value, value: form[key] !== undefined ? form[key] : value.value };
      } else if (typeof value === "object" && !Array.isArray(value)) {
        result[key] = mapValues(form[key] || {}, value);
      } else {
        result[key] = value;
      }
      return result;
    }, {});

  const formatDataStringToNumber = convertSpecificKeys(formData);

  const mappedData = Object.keys(dataDetailClone.input_data).reduce((result, key) => {
    result[key] = formData[key]
      ? mapValues(formatDataStringToNumber[key], dataDetailClone.input_data[key])
      : dataDetailClone.input_data[key];
    return result;
  }, {});

  const mergeInputData = (baseInputData, additionalInputData) => {
    const inputData = {
      main: { ...baseInputData?.main, ...additionalInputData?.main },
      vehicle: { ...baseInputData?.vehicle, ...additionalInputData?.vehicle },
      environment: { ...baseInputData?.environment, ...additionalInputData?.environment },
      initial_condition: { ...baseInputData?.initial_condition, ...additionalInputData?.initial_condition },
      target: { ...baseInputData?.target, ...additionalInputData?.target },
    };
    if (formData?.type === 1) {
      inputData.MCS = { ...baseInputData?.MCS, ...additionalInputData?.MCS };
    }
    return inputData;
  };

  const result = {
    ...dataDetailClone,
    name: formData.name || dataDetailClone.name,
    type: formData.type === 1 ? SIMULATION_TYPE.GNC_6DoF_MCS : SIMULATION_TYPE.GNC_6DOF,
    version_name: formData.version_name || dataDetailClone.version_name,
    note: formData.note || dataDetailClone.note,
    input_data: {
      ...dataDetailClone.input_data,
      ...mergeInputData(dataDetailClone.input_data, mappedData),
    },
    series: {
      parameter_path: extractString(formData.series[0]?.parameter_path[0], UPLOAD_TYPE.GNC_6DOF, "?AWSAccessKeyId"),
      aerodynamic_coefficients_path: extractString(
        formData.series[0]?.aerodynamic_coefficients_path[0],
        UPLOAD_TYPE.GNC_6DOF,
        "?AWSAccessKeyId"
      ),
    },
    processAsRoot: formData.processAsRoot,
  };

  if (result?.type === SIMULATION_TYPE.GNC_6DOF && result?.input_data?.MCS?.MCS_flag) {
    result.input_data.MCS.MCS_flag.value = 0;
    return result;
  }
  if (result?.type === SIMULATION_TYPE.GNC_6DoF_MCS) {
    result.input_data.MCS.MCS_flag.value = 1;
    return result;
  }

  return result;
};

/**
 * Handles exception fields by adding them to the item object.
 * Supports both single exception field (string) and multiple exception fields (array).
 *
 * @param {Object} item - The item object to add exception fields to
 * @param {string|string[]|null} exceptionField - A field name or array of field names to include as exception fields
 * @param {Object} filesUploadData - The source data containing the field values
 * @returns {Object} The item object with exception fields added
 */
const handleExceptionFields = (item, exceptionField, filesUploadData) => {
  if (!exceptionField) {
    return item;
  }

  if (Array.isArray(exceptionField)) {
    exceptionField.forEach((field) => {
      item[field] = filesUploadData?.[field] || "";
    });
  } else {
    item[exceptionField] = filesUploadData?.[exceptionField] || "";
  }

  return item;
};

/**
 * Converts an object containing uploaded file data into an array of objects,
 * each representing a set of files for a simulation run, based on the simulation type.
 *
 * For fields with only one file, the file is reused for all items. For fields with multiple files,
 * each item in the result array will contain the corresponding file by index. If a field is missing
 * or has fewer files than the maximum, an empty string is used as a placeholder.
 * For 6DOF simulations, adds a TARGET_MODE field to each item.
 *
 * @param {Object} filesUploadData - An object mapping field names to arrays of uploaded files.
 * @param {string} type - The simulation type, e.g., SIMULATION_TYPE["3DOF"] or SIMULATION_TYPE["6DOF"].
 * @param {string|string[]|null} exceptionField - A field name or array of field names to include as exception fields.
 * @returns {Array<Object>} An array of objects, each containing the merged file data for a simulation run.
 */
const convertFilesUploadDataToArray = (filesUploadData, type, exceptionField = null) => {
  if (!filesUploadData) return [];

  // Define field lists for each simulation type
  const fieldLists = {
    [SIMULATION_TYPE["3DOF"]]: [
      FORM_NAME_3DOF.PARAMETER_JSON,
      FORM_NAME_3DOF.AERO_COEFFICIENTS_1,
      FORM_NAME_3DOF.AERO_COEFFICIENTS_2,
      FORM_NAME_3DOF.WIND_MODEL,
      FORM_NAME_3DOF.THRUST_PROFILE,
    ],
    [SIMULATION_TYPE["6DOF"]]: [
      FORM_NAME_6DOF.PARAMETER_JSON,
      FORM_NAME_6DOF.MASS_AND_SHAPE_RESPONSE,
      FORM_NAME_6DOF.AERO_COEFFICIENTS_ASCENT,
      FORM_NAME_6DOF.AERO_COEFFICIENTS_ASCENT_2,
      FORM_NAME_6DOF.AERO_COEFFICIENTS_DESCENT,
      FORM_NAME_6DOF.WIND_MODEL,
      FORM_NAME_6DOF.THRUST_PROFILE,
      FORM_NAME_6DOF.TARGET_ATTITUDE_ASCENT,
      FORM_NAME_6DOF.TARGET_ATTITUDE_DESCENT,
      FORM_NAME_6DOF.TARGET_ATTITUDE_RATE_ASCENT,
      FORM_NAME_6DOF.TARGET_ATTITUDE_RATE_DESCENT,
    ],
  };

  const fields = fieldLists[type];
  if (!fields) return [];

  // Find the maximum length among all file arrays for the fields
  const maxLength = fields.reduce((max, field) => {
    const arr = filesUploadData[field];
    return Array.isArray(arr) ? Math.max(max, arr.length) : max;
  }, 0);

  // For fields with only one file, use that file for all items
  const singleFileFields = fields.reduce((acc, field) => {
    const arr = filesUploadData[field];
    if (Array.isArray(arr) && arr.length === 1) {
      acc[field] = arr[0];
    }
    return acc;
  }, {});

  // Build the merged array of file data
  const result = Array.from({ length: maxLength }, (_, i) => {
    const item = {};

    fields.forEach((field) => {
      if (singleFileFields[field] !== undefined) {
        item[field] = singleFileFields[field];
        return;
      }
      if (Array.isArray(filesUploadData[field])) {
        item[field] = filesUploadData[field][i] || "";
        return;
      }
      item[field] = "";
    });

    if (exceptionField) {
      handleExceptionFields(item, exceptionField, filesUploadData);
    }

    // For 6DOF, add TARGET_MODE field
    item[FORM_NAME_6DOF.TARGET_MODE] =
      type === SIMULATION_TYPE["6DOF"] ? (filesUploadData?.[FORM_NAME_6DOF.TARGET_MODE] === 0 ? 0 : 1) : undefined;

    return item;
  });

  return result;
};

const removeDuplicateValues = (data) => {
  const result = {};

  Object.entries(data).forEach(([key, value]) => {
    if (!Array.isArray(value)) {
      result[key] = value;
      return;
    }

    // If array contains objects, deduplicate by JSON stringification
    if (value.some((item) => typeof item === "object" && item !== null)) {
      const seen = new Set();
      result[key] = value.filter((item) => {
        const identifier = JSON.stringify(item);
        if (seen.has(identifier)) return false;
        seen.add(identifier);
        return true;
      });
    } else {
      // For arrays of primitives, deduplicate using Set
      result[key] = Array.from(new Set(value));
    }
  });

  return result;
};

const getInitialResultObject = (config) => {
  const result = {};

  config?.arrayFields?.forEach((field) => {
    result[field] = [];
  });

  config?.stringFields?.forEach((field) => {
    result[field] = TARGET_MODE.RATE;
  });

  return result;
};

const convertParamsInputToObject = (array, type) => {
  const fieldConfigs = {
    [SIMULATION_TYPE["3DOF"]]: {
      arrayFields: [
        FORM_NAME_3DOF.PARAMETER_JSON,
        FORM_NAME_3DOF.AERO_COEFFICIENTS_1,
        FORM_NAME_3DOF.AERO_COEFFICIENTS_2,
        FORM_NAME_3DOF.WIND_MODEL,
        FORM_NAME_3DOF.THRUST_PROFILE,
      ],
      stringFields: [],
    },
    [SIMULATION_TYPE["6DOF"]]: {
      arrayFields: [
        FORM_NAME_6DOF.PARAMETER_JSON,
        FORM_NAME_6DOF.MASS_AND_SHAPE_RESPONSE,
        FORM_NAME_6DOF.AERO_COEFFICIENTS_ASCENT,
        FORM_NAME_6DOF.AERO_COEFFICIENTS_ASCENT_2,
        FORM_NAME_6DOF.AERO_COEFFICIENTS_DESCENT,
        FORM_NAME_6DOF.WIND_MODEL,
        FORM_NAME_6DOF.THRUST_PROFILE,
        FORM_NAME_6DOF.TARGET_ATTITUDE_ASCENT,
        FORM_NAME_6DOF.TARGET_ATTITUDE_DESCENT,
        FORM_NAME_6DOF.TARGET_ATTITUDE_RATE_ASCENT,
        FORM_NAME_6DOF.TARGET_ATTITUDE_RATE_DESCENT,
      ],
      stringFields: [FORM_NAME_6DOF.TARGET_MODE],
    },
  };

  if (!Array.isArray(array)) {
    return getInitialResultObject(fieldConfigs[type]);
  }

  const config = fieldConfigs[type];
  const result = getInitialResultObject(config);

  array.forEach((item) => {
    config?.arrayFields?.forEach((field) => {
      if (item?.[field]) {
        result[field].push(
          item[field]?.name ? { name: getFileName(item[field]?.name), size: item[field]?.size } : getFileName(item[field])
        );
      }
    });

    config?.stringFields?.forEach((field) => {
      if (field === FORM_NAME_6DOF.TARGET_MODE) {
        result[field] = item[field];
      }
    });
  });

  return removeDuplicateValues(result);
};

/**
 * Updates the state indicating whether each field in `uploadFiles` contains multiple files.
 * If no field contains multiple files, all fields in the state are set to `true`.
 *
 * @param {Object} uploadFiles - An object where keys represent field names and values are arrays of files.
 * @param {Object} uploadMultipleState - An object representing the current state of multiple file uploads.
 * @returns {Object} The updated state indicating whether each field has multiple files.
 */
const updateUploadMultipleState = (uploadFiles, uploadMultipleState) => {
  const updatedState = { ...uploadMultipleState };

  let hasMultipleFiles = false;

  Object.keys(uploadFiles).forEach((field) => {
    const fieldValues = uploadFiles[field] || [];
    updatedState[field] = fieldValues.length > 1;

    if (fieldValues.length > 1) {
      hasMultipleFiles = true;
    }
  });

  if (!hasMultipleFiles) {
    Object.keys(updatedState).forEach((field) => {
      updatedState[field] = true;
    });
  }

  return updatedState;
};

/**
 * Maps files for array fields with special handling
 * @param {Array} fileArray - Array of files to map
 * @param {Array} res - Available files to match from
 * @param {Set} usedFiles - Set to track used files
 * @param {boolean} isSpecialField - Whether this field requires unique file mapping
 * @returns {Array} Mapped file array
 */
const mapArrayField = (fileArray, res, usedFiles, isSpecialField) => {
  return fileArray.map((f) => {
    const trackingSet = isSpecialField ? usedFiles : new Set();
    const match = getMatchFile(res, f, trackingSet);

    if (match && isSpecialField) {
      usedFiles.add(match);
      return match;
    }

    return match || f;
  });
};

/**
 * Maps files for single file fields
 * @param {Object} fileValue - File to map
 * @param {Array} res - Available files to match from
 * @param {Set} usedFiles - Set to track used files
 * @returns {Object|null} Mapped file or null
 */
const mapSingleField = (fileValue, res, usedFiles) => {
  const matchingFile = getMatchFile(res, fileValue, usedFiles);

  if (matchingFile) {
    usedFiles.add(matchingFile);
    return matchingFile;
  }

  return null;
};

/**
 * Processes a single field based on its type and configuration
 * @param {string} key - Field key
 * @param {any} fieldValue - Field value to process
 * @param {Array} res - Available files to match from
 * @param {Set} usedFiles - Set to track used files
 * @param {Array} fieldKeyArray - Array of special fields
 * @returns {any} Processed field value
 */
const processField = (key, fieldValue, res, usedFiles, fieldKeyArray) => {
  if (key === FORM_NAME_6DOF.TARGET_MODE || !fieldValue) {
    return fieldValue;
  }

  if (Array.isArray(fieldValue)) {
    const isSpecialField = fieldKeyArray.includes(key);
    return mapArrayField(fieldValue, res, usedFiles, isSpecialField);
  }

  const mappedFile = mapSingleField(fieldValue, res, usedFiles);
  return mappedFile || fieldValue;
};

/**
 * Processes a single file object by mapping all its fields
 * @param {Object} file - File object to process
 * @param {Array} res - Available files to match from
 * @param {Set} usedFiles - Set to track used files
 * @param {Array} fieldKeyArray - Array of special fields
 * @returns {Object} Processed file object
 */
const processFileObject = (file, res, usedFiles, fieldKeyArray) => {
  const updatedFile = { ...file };

  Object.keys(file).forEach((key) => {
    const processedValue = processField(key, file[key], res, usedFiles, fieldKeyArray);

    if (processedValue !== file[key]) {
      updatedFile[key] = processedValue;
    }
  });

  return updatedFile;
};

/**
 * Maps uploaded files to form data with configurable special field handling
 * @param {Array} allFiles - Array of file objects to process
 * @param {Array} res - Array of available files to match from
 * @param {Array} fieldKeyArray - Array of field names requiring unique file mapping
 * @returns {Array} Array of processed file objects
 */
const mapUploadedFilesToFormData = (allFiles, res, fieldKeyArray = []) => {
  const usedFiles = new Set();

  return allFiles.map((file) => processFileObject(file, res, usedFiles, fieldKeyArray));
};

const getMatchFile = (findList, file, usedFiles) => {
  return findList.find((f) => {
    const isMatch = getFileName(f?.name) === (file?.name || file) && (!file?.size || f?.size === file.size) && !usedFiles.has(f);
    return isMatch;
  });
};

const isDuplicateFileName = (initialFiles, fileName) => {
  if (isEmpty(initialFiles)) return false;
  const existingFiles = initialFiles.map((file) => file?.name || file);

  return existingFiles.some((existing) => {
    const existingFileName = existing?.name || existing;
    return existingFileName.trim().toLowerCase() === fileName.trim().toLowerCase();
  });
};

/**
 * Clone a File properly (not by reference).
 * @param {File} file
 * @returns {File}
 */
const cloneFile = (file) => new File([file], file.name, { type: file.type });

/**
 * Duplicate uploaded files to ensure each field in formData has the same number of files.
 *
 * @param {Object<string, string[]>} formData - Object mapping field names to arrays of file names
 * @param {File[]} uploadedFiles - Array of uploaded File objects
 * @param {string[]} fieldKeyArray - Array of field names that should have special handling (multiply by max length)
 * @returns {File[]} - Flattened array of File objects, with duplicates to match max array length
 */
const duplicateFiles = (formData, uploadedFiles, fieldKeyArray = []) => {
  const formDataClone = cloneDeep(formData);
  delete formDataClone?.[FORM_NAME_6DOF.TARGET_MODE];

  const entries = Object.entries(formDataClone);

  // Calculate max length excluding special fields for special handling
  const entriesExcludingSpecialFields = entries.filter(([key]) => !fieldKeyArray.includes(key));
  const maxLength = max(map(entriesExcludingSpecialFields, ([, arr]) => arr.length)) || 0;

  const result = flatMap(entries, ([key, valueArray]) => {
    const matchedFiles = valueArray
      ?.map((file) =>
        uploadedFiles.find((f) => {
          if (file?.name) {
            // Check both name and size if available
            return f.name === file.name && f.size === file.size;
          }
          // If file is a string (filename), try to match by name and size if possible
          if (typeof file === "string") {
            // Try to find a file with the same name (and size if available)
            return f.name === file || (f.name === file?.name && (!file?.size || f.size === file.size));
          }
          return false;
        })
      )
      .filter(Boolean); // removes undefined

    let targetLength;
    let clones = [];

    if (fieldKeyArray.includes(key) && valueArray.length > 0) {
      // Special handling for fields in fieldKeyArray: multiply by max length
      targetLength = maxLength * valueArray.length;
      const totalClonesNeeded = targetLength - matchedFiles.length;

      if (totalClonesNeeded > 0 && matchedFiles.length > 0) {
        clones = times(totalClonesNeeded, (i) => cloneFile(matchedFiles[i % matchedFiles.length]));
      }
    } else {
      // Regular handling for other fields
      const missingCount = maxLength - valueArray.length;

      if (missingCount > 0 && matchedFiles.length > 0) {
        clones = times(missingCount, (i) => cloneFile(matchedFiles[i % matchedFiles.length]));
      }
    }

    return [...matchedFiles, ...clones];
  });

  return result;
};

/**
 * Determines the appropriate file path to clone based on the provided value
 * @param {string} value - The file path or file name
 * @param {Object} fallbackItem - The fallback item to get the full path from
 * @param {string} key - The key to look up in the fallback item
 * @returns {string} The file path to clone
 */
const getFilePathToClone = (value, fallbackItem, key) => {
  if (value.includes("/") || !fallbackItem || !fallbackItem[key]) {
    return value;
  }

  return fallbackItem[key];
};

/**
 * Generates new file paths for all string fields in an item
 * @param {Object} item - The item containing file paths
 * @param {Function} generateFilePath - The API function to generate file paths
 * @param {Object} fallbackItem - The fallback item for reference paths
 * @returns {Promise<Object>} Object with new file paths
 */
const generateNewFilePaths = async (item, generateFilePath, fallbackItem) => {
  const newFilePaths = {};

  for (const [key, value] of Object.entries(item)) {
    if (value) {
      const filePathToClone = getFilePathToClone(value?.name || value, fallbackItem, key);
      const apiResponse = await generateFilePath(filePathToClone);
      newFilePaths[key] = apiResponse.name;
    } else {
      newFilePaths[key] = value?.name || value;
    }
  }

  return newFilePaths;
};

/**
 * Gets the raw item for processing, either from existing data or by generating new file paths
 * @param {number} index - The current index
 * @param {number} rawItemCount - The count of raw items available
 * @param {Object} item - The current item being processed
 * @param {Array} seriesDataRaw - The raw series data array
 * @param {Function} generateFilePath - The API function to generate file paths
 * @returns {Promise<Object>} The raw item with new file paths to use for processing
 */
const getRawItemForProcessing = async (index, rawItemCount, item, seriesDataRaw, generateFilePath) => {
  if (index < rawItemCount) {
    return seriesDataRaw[index];
  }

  if (!generateFilePath) {
    return seriesDataRaw[0]; // fallback to first item
  }

  try {
    const newFilePaths = await generateNewFilePaths(item, generateFilePath, seriesDataRaw[0]);
    return { ...item, ...newFilePaths };
  } catch (error) {
    return seriesDataRaw[0]; // fallback to first item
  }
};

/**
 * Maps file paths between an item and its corresponding raw item
 * @param {Object} item - The item to process
 * @param {Object} rawItem - The raw item containing reference file paths
 * @returns {Object} The processed item with mapped file paths
 */
const mapFilePathsFromRawItem = (item, rawItem) => {
  const result = { ...item };

  Object.entries(item).forEach(([key, value]) => {
    if (typeof value !== "string") return;

    const fileName = getFileName(value);
    const rawValue = rawItem[key];

    if (typeof rawValue === "string") {
      const rawFileName = getFileName(rawValue);
      const isValuePath = value.includes("/");
      const isRawPath = rawValue.includes("/");

      if (rawFileName === fileName) {
        result[key] = isRawPath ? rawValue : isValuePath ? value : fileName;
      }
    }
  });

  return result;
};

/**
 * Processes a single item in the series data
 * @param {Object} item - The item to process
 * @param {number} index - The index of the item
 * @param {Array} seriesDataRaw - The raw series data array
 * @param {Function} generateFilePath - The API function to generate file paths
 * @returns {Promise<Object>} The processed item
 */
const processSingleSeriesItem = async (item, index, seriesDataRaw, generateFilePath) => {
  const rawItemCount = seriesDataRaw.length;
  const rawItem = await getRawItemForProcessing(index, rawItemCount, item, seriesDataRaw, generateFilePath);

  if (!rawItem) {
    return { ...item };
  }

  return mapFilePathsFromRawItem(item, rawItem);
};

/**
 * Maps prefix in series data by processing file paths and generating new ones when needed
 * @param {Array} seriesDataRaw - The raw series data array
 * @param {Array} seriesData - The series data to process
 * @param {Function} generateFilePath - API function to generate file paths
 * @returns {Promise<Array>} The processed series data with mapped file paths
 */
const mappingPrefixInSeriesData6DOF = async (seriesDataRaw, seriesData, generateFilePath) => {
  if (!Array.isArray(seriesDataRaw) || !Array.isArray(seriesData)) {
    return seriesData || [];
  }

  const results = await Promise.all(seriesData.map((item, index) => processSingleSeriesItem(item, index, seriesDataRaw, generateFilePath)));

  return results;
};

const mappingPrefixInSeriesData = (seriesDataRaw, seriesData) => {
  if (!Array.isArray(seriesDataRaw) || !Array.isArray(seriesData)) return seriesData || [];

  const rawItemCount = seriesDataRaw.length;

  return seriesData.map((item, index) => {
    const result = { ...item };
    const rawItem = seriesDataRaw[index] || (index >= rawItemCount ? seriesDataRaw[0] : null);

    if (!rawItem) return result;

    Object.entries(item).forEach(([key, value]) => {
      if (typeof value !== "string") return;

      const fileName = getFileName(value);
      const rawValue = rawItem[key];
      if (typeof rawValue === "string") {
        const rawFileName = getFileName(rawValue);
        const isValuePath = value.includes("/");
        const isRawPath = rawValue.includes("/");

        if (rawFileName === fileName) {
          result[key] = isRawPath ? rawValue : isValuePath ? value : fileName;
        }
      }
    });

    return result;
  });
};
/**
 * Filters and transforms an array of uploaded files based on the specified target mode.
 *
 * Depending on the mode, this function removes files with certain keys and, for specific keys,
 * returns only their values instead of the entire object.
 *
 * @param {Array<Object>} uploadedFiles - The array of uploaded file objects to filter.
 * @param {string} mode - The target mode to filter by (e.g., TARGET_MODE.RATE).
 * @returns {Array<Object|any>} The filtered and transformed array of files.
 */
const filterUploadedFilesByTargetMode = (uploadedFiles, mode) => {
  if (!Array.isArray(uploadedFiles)) return [];

  let filteredFiles;
  if (mode === TARGET_MODE.RATE) {
    filteredFiles = uploadedFiles.filter(
      (file) => ![FORM_NAME_6DOF.TARGET_ATTITUDE_ASCENT, FORM_NAME_6DOF.TARGET_ATTITUDE_DESCENT].includes(Object.keys(file)[0])
    );
    return filteredFiles.map((file) => {
      const key = Object.keys(file)[0];
      if ([FORM_NAME_6DOF.TARGET_ATTITUDE_RATE_ASCENT, FORM_NAME_6DOF.TARGET_ATTITUDE_RATE_DESCENT].includes(key)) {
        return file[key];
      }
      return file;
    });
  } else {
    filteredFiles = uploadedFiles.filter(
      (file) => ![FORM_NAME_6DOF.TARGET_ATTITUDE_RATE_ASCENT, FORM_NAME_6DOF.TARGET_ATTITUDE_RATE_DESCENT].includes(Object.keys(file)[0])
    );
    return filteredFiles.map((file) => {
      const key = Object.keys(file)[0];
      if ([FORM_NAME_6DOF.TARGET_ATTITUDE_ASCENT, FORM_NAME_6DOF.TARGET_ATTITUDE_DESCENT].includes(key)) {
        return file[key];
      }
      return file;
    });
  }
};

const removeFieldOrObject = (fieldNameSelected, fileNameSelected, rawData, uploadMultiple) => {
  const isMultiple = uploadMultiple[fieldNameSelected];
  const fileNames = Array.isArray(fileNameSelected) ? fileNameSelected : [fileNameSelected];
  const result = cloneDeep(rawData);

  if (isMultiple) {
    if (result.length === 1) {
      // Only one object → just set the field to null if matched
      return result.map((item) => {
        const fieldValue = item[fieldNameSelected];
        const shouldClear = fieldValue && fileNames.some((fileName) => endsWith(fieldValue, fileName));
        if (shouldClear) {
          item[fieldNameSelected] = null;
        }
        return item;
      });
    } else {
      // Multiple objects → filter out objects that MATCH the file name
      return result.filter((item) => {
        const fieldValue = item[fieldNameSelected];
        return !fileNames.some((fileName) => endsWith(fieldValue, fileName));
      });
    }
  } else {
    // Single field → set field to null if matched
    return result.map((item) => {
      const fieldValue = item[fieldNameSelected];
      const shouldClear = fieldValue && fileNames.some((fileName) => endsWith(fieldValue, fileName));
      if (shouldClear) {
        item[fieldNameSelected] = null;
      }
      return item;
    });
  }
};

/**
 * Extracts filename from a value that could be a string or object with name property
 * @param {any} value - The value to extract filename from
 * @returns {string} The extracted filename
 */
const extractFileName = (value) => {
  if (typeof value === "object" && value?.name) {
    return getFileName(value.name);
  }
  return getFileName(value);
};

/**
 * Compares two field values, handling both arrays and non-arrays
 * @param {any} newValue - Value from new data
 * @param {any} rootValue - Value from root data
 * @returns {boolean} True if values are considered equal
 */
const compareFieldValues = (newValue, rootValue) => {
  const isNewArray = Array.isArray(newValue);
  const isRootArray = Array.isArray(rootValue);

  // Both are arrays
  if (isNewArray && isRootArray) {
    if (newValue.length !== rootValue.length) {
      return false;
    }

    const fileNames1 = newValue.map(extractFileName).sort();
    const fileNames2 = rootValue.map(extractFileName).sort();

    return fileNames1.every((fileName, index) => fileName === fileNames2[index]);
  }

  // One is array, one is not
  if (isNewArray || isRootArray) {
    return false;
  }

  // Both are non-arrays - compare as file values
  return extractFileName(newValue) === extractFileName(rootValue);
};

/**
 * Finds duplicate items between root data and new data
 * @param {Array} rootData - Array of existing items
 * @param {Array} newData - Array of new items to check for duplicates
 * @returns {Array} Array of duplicate items from newData
 */
const findDuplicates = (rootData, newData) => {
  return newData.filter((newItem) => {
    return rootData.some((rootItem) => {
      return Object.keys(newItem).every((key) => {
        const newValue = newItem[key];
        const rootValue = rootItem[key];
        return compareFieldValues(newValue, rootValue);
      });
    });
  });
};

const truncateFileName = (fileName, maxLength = 30) => {
  if (!fileName || fileName.length <= maxLength) return fileName;
  const extension = fileName.split(".").pop();
  const baseName = fileName.slice(0, maxLength - extension.length - 3);
  return `${baseName}...${extension}`;
};

const safeStringify = (obj) => {
  try {
    return JSON.stringify(obj || {}, null, 4);
  } catch (error) {
    console.error("JSON stringify failed:", error);
    return "{}";
  }
};

const parseStringToNumberArray = (x, y, z) => [
  typeof x === "string" ? x.split(",").map(Number) : x,
  typeof y === "string" ? y.split(",").map(Number) : y,
  typeof z === "string" ? z.split(",").map(Number) : z,
];

const extractValuesForSimMCS = (source, target, keys, data) => {
  if (data?.MCS?.[source]?.value) {
    keys.forEach((key, index) => {
      target[`${source}_${key}`] = { value: data.MCS[source].value[index] };
    });
  }
};

const extractFormValuesForSimMCS = (source, target, keys, data) => {
  if (data?.MCS?.[source]) {
    keys.forEach((key, index) => {
      target[`${source}_${key}`] = data?.MCS[source]?.[index];
    });
  }
};

const calculateMCSSuccessRate = (caseResultData) => {
  if (!caseResultData || caseResultData.length === 0) return 0;
  const totalCases = caseResultData.length;
  const successfulCases = caseResultData.filter((item) => Number(item["Flight status"]) === 1).length;
  if (successfulCases === 0) return 0;
  const successRate = (successfulCases / totalCases).toFixed(2);
  return successRate;
};

const compareProdVersions = (currentVersion, versionCheckpoint) => {
  const currentParts = parseVersion(currentVersion);
  const checkpointParts = parseVersion(versionCheckpoint);

  if (!currentParts || !checkpointParts || currentParts.type !== VERSION_TYPE.PROD || checkpointParts.type !== VERSION_TYPE.PROD) {
    return false;
  }

  if (currentParts.major !== checkpointParts.major) {
    return currentParts.major >= checkpointParts.major;
  }

  if (currentParts.minor !== checkpointParts.minor) {
    return currentParts.minor >= checkpointParts.minor;
  }

  return currentParts.patch >= checkpointParts.patch;
};

const compareDevVersions = (currentVersion, versionCheckpoint) => {
  const currentParts = parseVersion(currentVersion);
  const checkpointParts = parseVersion(versionCheckpoint);

  if (!currentParts || !checkpointParts || currentParts.type !== VERSION_TYPE.DEV || checkpointParts.type !== VERSION_TYPE.DEV) {
    return false;
  }

  if (currentParts.year !== checkpointParts.year) {
    return currentParts.year >= checkpointParts.year;
  }

  if (currentParts.month !== checkpointParts.month) {
    return currentParts.month >= checkpointParts.month;
  }

  if (currentParts.day !== checkpointParts.day) {
    return currentParts.day >= checkpointParts.day;
  }

  return currentParts.number >= checkpointParts.number;
};

const parseVersion = (versionString) => {
  if (versionString.startsWith("v") || versionString.startsWith("V")) {
    const cleanVersion = versionString.substring(1);
    const parts = cleanVersion.split(".").map(Number);
    return {
      type: VERSION_TYPE.PROD,
      major: parts[0] || 0,
      minor: parts[1] || 0,
      patch: parts[2] || 0,
    };
  }

  const match = versionString.match(PATTERN_SIMULATION_VERSION);
  if (match) {
    return {
      type: VERSION_TYPE.DEV,
      year: parseInt(match[1], 10),
      month: parseInt(match[2], 10),
      day: parseInt(match[3], 10),
      number: parseInt(match[4], 10),
    };
  }

  return null;
};

const isNewSimulationVersion = (currentVersion, compareVersion = REACT_APP_SIMULATION_3DOF_VERSION) => {
  if (!currentVersion || !compareVersion) return false;

  const currentVersionParts = parseVersion(currentVersion);
  const checkpointVersionParts = parseVersion(compareVersion);

  if (!currentVersionParts || !checkpointVersionParts) return false;

  // Both versions must be of the same type to compare
  if (currentVersionParts.type !== checkpointVersionParts.type) return false;

  if (currentVersionParts.type === VERSION_TYPE.PROD) {
    return compareProdVersions(currentVersion, compareVersion);
  }

  if (currentVersionParts.type === VERSION_TYPE.DEV) {
    return compareDevVersions(currentVersion, compareVersion);
  }

  return false;
};

const checkVersionSwitch = (previousVersion, currentVersion) => {
  if (!previousVersion || !currentVersion) {
    return { hasSwitched: false, switchType: null };
  }

  const wasPreviousNew = isNewSimulationVersion(previousVersion);
  const isCurrentNew = isNewSimulationVersion(currentVersion);

  // Check if there's a switch between old and new versions
  if (wasPreviousNew !== isCurrentNew) {
    const switchType = isCurrentNew ? "old-to-new" : "new-to-old";

    return { hasSwitched: true, switchType, wasPreviousNew, isCurrentNew };
  }

  return { hasSwitched: false, switchType: null };
};

const isCSV = (fileName) => {
  return fileName?.toLowerCase().endsWith(".csv");
};

const isXlsx = (fileName) => {
  return fileName?.toLowerCase().endsWith(".xlsx");
};

const isXlsm = (fileName) => {
  return fileName?.toLowerCase().endsWith(".xlsm");
};

export {
  humanFileSize,
  transformDataVersionsToOptions,
  getFileName,
  getFileType,
  uniqArray,
  handleScrollTo,
  sortData,
  uuid,
  extractIDsFromString,
  transformProcessDataToOptions,
  getConfigFromId,
  getConfigFileName,
  isCSVContent,
  handleScrollToTop,
  transformThrustCalculation,
  reverseTransformThrustCalculation,
  roundToTwoDecimals,
  mergeArrays,
  getChartData,
  uniqueArray,
  MAPPING_CONTENT_CHANGE_TO_TEXT,
  sumArrayWithKey,
  mergeChildren,
  findMaxMin,
  calculateRocketLength,
  calculateProperties,
  calculateCenterOfGravity,
  formatStringToNumber,
  deepEqual,
  getClusterCenters,
  prepareData6DOF,
  mapFormDataToDefaultData6DOF,
  truncateFileName,
  getFileUploadName,
  safeStringify,
  parseStringToNumberArray,
  extractValuesForSimMCS,
  calculateMCSSuccessRate,
  extractFormValuesForSimMCS,
  convertFilesUploadDataToArray,
  convertParamsInputToObject,
  updateUploadMultipleState,
  removeDuplicateValues,
  mapUploadedFilesToFormData,
  compareProdVersions,
  compareDevVersions,
  parseVersion,
  isNewSimulationVersion,
  checkVersionSwitch,
  isDuplicateFileName,
  duplicateFiles,
  mappingPrefixInSeriesData,
  mappingPrefixInSeriesData6DOF,
  filterUploadedFilesByTargetMode,
  cloneFile,
  removeFieldOrObject,
  getFilePathToClone,
  generateNewFilePaths,
  getRawItemForProcessing,
  mapFilePathsFromRawItem,
  processSingleSeriesItem,
  findDuplicates,
  extractFileName,
  compareFieldValues,
  isCSV,
  isXlsx,
  isXlsm,
  scrollToLatestTag,
};
